"use client";

import { ArrowRight, Check, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const expectations = ["Initial creative discovery session", "Customized production roadmap", "Full-scale campaign strategy review"];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-primary/40 bg-primary/5">
        <p className="font-default text-lg text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div>
        <p className="font-default text-[11px] uppercase tracking-[0.2em] text-primary mb-6">
          Section A — Client Details
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="full_name" rules={{
          required: "Full name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Full Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" className="bg-transparent border-0 border-b border-border px-0 h-10 text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email_address" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@agency.com" className="bg-transparent border-0 border-b border-border px-0 h-10 text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <FormField control={control} name="phone_number" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Phone Number
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-transparent border-0 border-b border-border px-0 h-10 text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="preferred_date" rules={{
          required: "Preferred date is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Preferred Date
                </FormLabel>
                <FormControl>
                  <Input type="date" className="bg-transparent border-0 border-b border-border px-0 h-10 text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <div>
        <p className="font-default text-[11px] uppercase tracking-[0.2em] text-primary mb-6">
          Section B — Project Scope
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="brand_name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Brand Name (Optional)
                </FormLabel>
                <FormControl>
                  <Input placeholder="Your Brand" className="bg-transparent border-0 border-b border-border px-0 h-10 text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="service_interest" rules={{
          required: "Please select a service"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Primary Interest
                </FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="bg-transparent border-0 border-b border-border px-0 h-10 text-base font-default text-foreground focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary w-full">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="Creative Direction">Creative Direction</SelectItem>
                      <SelectItem value="Brand Storytelling">Brand Storytelling</SelectItem>
                      <SelectItem value="Video Production">Video Production</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="mt-6">
          <FormField control={control} name="additional_notes" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Project Brief
                </FormLabel>
                <FormControl>
                  <Textarea placeholder="Tell us about your vision..." className="bg-transparent border-0 border-b border-border px-0 min-h-[120px] text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary resize-none" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Checkbox id="newsletter-subscribe" className="border-muted-foreground" />
        <label htmlFor="newsletter-subscribe" className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground cursor-pointer">
          Subscribe to our creative updates
        </label>
      </div>

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-sm uppercase tracking-[0.2em] font-semibold">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Submit Request
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function LetSCreate() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3963" id="lets-create" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-20 relative overflow-hidden">
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] rounded-lg opacity-[0.05] blur-[120px] pointer-events-none" style={{
      backgroundColor: "var(--primary)"
    }} aria-hidden="true" />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-default text-[11px] uppercase tracking-[0.3em] text-primary mb-8">[ CONTACT ]</p>

            <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-10">
              Let's
              <br />
              create
              <span className="text-primary">.</span>
            </h2>

            <p className="font-default text-lg leading-relaxed text-muted-foreground mb-12 max-w-md">
              Every campaign begins with a rigorous discovery phase. We align your brand objectives with cinematic execution to ensure every frame serves a strategic purpose.
            </p>

            <div>
              <p className="font-default text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-6">
                Our Process
              </p>
              <ul className="space-y-4">
                {expectations.map((item, i) => <li data-index={i} key={i} className="flex items-start gap-4">
                    <Check className="size-4 text-primary mt-1 shrink-0" strokeWidth={2.5} />
                    <span className="font-default text-sm uppercase tracking-[0.15em] text-foreground">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="mt-16 pt-8 border-t border-border">
              <p className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
                Direct Studio Line
              </p>
              <p className="font-default text-base text-foreground">+1 (555) 888-9999</p>
              <p className="font-default text-base text-foreground">hello@vividstudio.com</p>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:pl-8 transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="project_brief_form_start_project" formId="project_brief_form_start_project" className="relative p-8 md:p-14 lg:p-[60px] border border-border" sectionName="lets-create" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            preferred_date: "",
            service_interest: "",
            brand_name: "",
            additional_notes: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true,
              pattern: "phone"
            },
            preferred_date: {
              required: true
            },
            service_interest: {
              required: true
            }
          }} submitText="Submit Request" successMessage="Your inquiry has been received. Our production team will review your brief and reach out within 24 hours." errorMessage="Submission failed. Please verify your details and try again.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}